import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Screen } from '../../app/Frame';
import { useMeContext, useWritable } from '../../app/MeContext';
import { useScreen, useToast } from '../../app/shell-context';
import { ApiError } from '../../api/client';
import { weekdayRu } from '../../lib/time';
import { hoursSummary, sameHours } from '../../lib/org';
import { FormSkeleton, LoadFailed, StickySave, useSaveOrLeave } from '../org-parts';
import { TimePick } from '../../ui/parts';
import { useAdminSettings, useSaveSettings } from '../api';
import type { WorkingDay } from '../../api/types';

/**
 * Подэкран «Часы работы». Это часы ОРГАНИЗАЦИИ — справочная строка для клиента
 * и ориентир для администратора; сетка свободных окон по-прежнему строится из
 * личного расписания специалиста, и здешние правки её не двигают.
 */
export function OrgHours() {
  const writable = useWritable();
  const navigate = useNavigate();
  const toast = useToast();
  const data = useAdminSettings();
  const save = useSaveSettings();
  const tf = useMeContext().center.timeFormat;

  const [draft, setDraft] = useState<WorkingDay[] | null>(null);

  const loaded = data.data?.hours ?? null;
  const rows = draft ?? loaded;
  const dirty = !!rows && !!loaded && !sameHours(rows, loaded);
  const busy = save.isPending;

  useScreen('Часы работы', null);
  useSaveOrLeave(dirty, () => submit());

  if (data.isError) {
    return <Screen><LoadFailed busy={data.isFetching} onRetry={() => void data.refetch()} /></Screen>;
  }
  if (!rows) {
    return <Screen><FormSkeleton rows={7} /></Screen>;
  }

  const locked = !writable || busy;
  const broken = (r: WorkingDay) => !!r.on && r.to <= r.from;
  const hasBroken = rows.some(broken);
  const firstOpen = rows.find((r) => r.on);

  const patch = (i: number, p: Partial<WorkingDay>) =>
    setDraft(rows.map((r, k) => (k === i ? { ...r, ...p } : r)));

  /** Берём первый включённый день за образец: обычно им и задают всю неделю. */
  const copyToAll = () => {
    if (!firstOpen) return;
    setDraft(rows.map((r) => ({ ...r, on: 1, from: firstOpen.from, to: firstOpen.to })));
  };

  /**
   * Уходят только часы работы — своим полем `hours`. Остальные настройки
   * организации запрос не упоминает и потому не перезаписывает.
   *
   * Обещание возвращается наружу: сторож ухода с экрана ждёт ответа сервера,
   * прежде чем уводить, — иначе мутация уехала бы вместе с экраном.
   */
  function submit(): Promise<unknown> {
    if (!rows) return Promise.resolve();
    if (hasBroken) {
      toast('Время окончания должно быть позже начала');
      return Promise.reject(new Error('VALIDATION'));
    }
    return save.mutateAsync({ hours: rows }).then(
      () => {
        setDraft(null);
        toast('Изменения сохранены', { ms: 2000 });
        navigate('/admin/org/schedule');
      },
      (e: unknown) => {
        toast(e instanceof ApiError ? e.message : 'Не получилось сохранить');
        throw e;
      },
    );
  }

  return (
    <Screen className="sticky-save">
      <p className="lead">
        Справочные часы организации: показываем клиенту и держим перед глазами. Свободные окна
        для записи по-прежнему строятся из расписания специалиста.
      </p>

      <div className="hours">
        {rows.map((r, i) => (
          <div className={`hrow${r.on ? '' : ' is-off'}${broken(r) ? ' is-bad' : ''}`} key={r.d}>
            <span className="wd">{weekdayRu(r.d)}</span>
            {r.on ? (
              <span className="times">
                <TimePick
                  value={r.from}
                  label={`${weekdayRu(r.d, 'long')}, начало`}
                  format={tf}
                  disabled={locked}
                  onChange={(v) => patch(i, { from: v })}
                />
                {' — '}
                <TimePick
                  value={r.to}
                  label={`${weekdayRu(r.d, 'long')}, конец`}
                  format={tf}
                  disabled={locked}
                  onChange={(v) => patch(i, { to: v })}
                />
              </span>
            ) : (
              <span className="times dayoff">Выходной</span>
            )}
            <button
              className="switch"
              type="button"
              aria-pressed={!!r.on}
              aria-label={`${weekdayRu(r.d, 'long')} — рабочий день`}
              disabled={locked}
              onClick={() => patch(i, { on: r.on ? 0 : 1 })}
            />
          </div>
        ))}
      </div>

      {hasBroken ? (
        <p className="err">Время окончания должно быть позже начала</p>
      ) : (
        <p className="hint">На основном экране это будет выглядеть так: {hoursSummary(rows, tf)}</p>
      )}

      {writable ? (
        <>
          <button className="add" type="button" disabled={!firstOpen || locked} onClick={copyToAll}>
            Скопировать на все дни
          </button>
          <StickySave
            dirty={dirty && !hasBroken}
            busy={busy}
            failed={save.isError}
            onClick={() => { void submit().catch(() => {}); }}
          />
        </>
      ) : null}
    </Screen>
  );
}
